/**
 * Per-student structured JSON export.
 * Consumes pre-computed analytics from StudentAnalyticsContext
 * instead of re-running segmentation.
 *
 * Usage:
 *   import { buildPerStudentExport, downloadJSON } from './buildStudentExport'
 *   const { analyticsById } = useContext(StudentAnalyticsContext)
 *   const data = buildPerStudentExport(analyticsById, students, sessionId)
 *   downloadJSON(data, `session_${sessionId}_student_export.json`)
 */

// ─── Build export for one student from pre-computed analytics ─────────────────

function buildStudentExport(userId, studentName, analytics, rawEvents) {
  const {
    filteredEvents, replayStartTime, replayDuration,
    visibleSegments, firstQueryEvent, firstQueryMs,
    sessionStats, testScoreProgression, userMessages,
  } = analytics

  const events = filteredEvents || []
  if (events.length === 0) return null

  const sessionStart = events[0].timestamp
  const sessionEnd = events[events.length - 1].timestamp

  const queryEvents = events.filter(e => e.type === 'CHAT_QUERY')
  const terminalRuns = events.filter(e => e.type === 'TERMINAL_RUN')
  const terminalErrors = events.filter(e => e.type === 'TERMINAL_ERROR')
  const testResults = events.filter(e => e.type === 'TEST_CASE_RESULT')

  const finalTest = testResults.length > 0 ? testResults[testResults.length - 1] : null
  const finalPassed = finalTest?.payload?.passed_count || 0
  const finalTotal = finalTest?.payload?.total_tests || 0
  const allPassed = finalTotal > 0 && finalPassed === finalTotal

  const segments = visibleSegments || []

  // ── 1. Labeled Segments ──
  const labeledSegments = segments.map((s, i) => ({
    segment_index: i,
    start_time: Math.round(s.startTime),
    end_time: Math.round(s.endTime),
    duration_ms: Math.round(s.endTime - s.startTime),
    behavior: (s.behavior?.label || s.behavior?.id || 'unknown').toLowerCase(),
    events_in_segment: events.filter(e => {
      const relTime = e.timestamp - replayStartTime
      return relTime >= s.startTime && relTime < s.endTime
    }).length,
  }))

  // ── 2. Query Windows ──
  const queryWindows = queryEvents.map((qEvent, qIdx) => {
    const queryTimestamp = qEvent.timestamp
    const queryContent = qEvent.payload?.text || qEvent.payload?.message || qEvent.payload?.content || ''
    const queryRelTime = queryTimestamp - replayStartTime

    const prevQueryTimestamp = qIdx > 0 ? queryEvents[qIdx - 1].timestamp : sessionStart
    const windowStartRel = qIdx > 0 ? (prevQueryTimestamp - replayStartTime) : 0
    const windowDuration = queryRelTime - windowStartRel

    // Behavior sequence from segments in this window
    const windowSegments = segments.filter(s => s.endTime > windowStartRel && s.startTime < queryRelTime)
    const behaviorSequence = windowSegments.map(s => {
      const start = Math.max(s.startTime, windowStartRel)
      const end = Math.min(s.endTime, queryRelTime)
      const label = (s.behavior?.label || s.behavior?.id || 'unknown').toLowerCase()
      return [label, Math.round(end - start)]
    })

    // Terminal runs & errors in window
    const windowRunCount = terminalRuns.filter(e =>
      e.timestamp >= prevQueryTimestamp && e.timestamp < queryTimestamp
    ).length
    const windowErrorCount = terminalErrors.filter(e =>
      e.timestamp >= prevQueryTimestamp && e.timestamp < queryTimestamp
    ).length

    // Test state before query
    const testsBefore = testResults.filter(t => t.timestamp <= queryTimestamp)
    const lastTest = testsBefore.length > 0 ? testsBefore[testsBefore.length - 1] : null

    // Previous query's dominant behavior pattern
    let previousPattern = null
    if (qIdx > 0) {
      const prevWindowStart = qIdx > 1 ? (queryEvents[qIdx - 2].timestamp - replayStartTime) : 0
      const prevWindowEnd = queryEvents[qIdx - 1].timestamp - replayStartTime
      const prevSegs = segments.filter(s => s.endTime > prevWindowStart && s.startTime < prevWindowEnd)
      if (prevSegs.length > 0) {
        const longest = prevSegs.reduce((a, b) => (b.endTime - b.startTime) > (a.endTime - a.startTime) ? b : a)
        previousPattern = (longest.behavior?.label || longest.behavior?.id || 'unknown').toLowerCase()
      }
    }

    // Consecutive no-effort count (how many queries in a row had zero code activity)
    let consecutiveNoEffort = 0
    for (let k = qIdx - 1; k >= 0; k--) {
      const pStart = k > 0 ? (queryEvents[k - 1].timestamp - replayStartTime) : 0
      const pEnd = queryEvents[k].timestamp - replayStartTime
      const pSegs = segments.filter(s => s.endTime > pStart && s.startTime < pEnd)
      const hadCode = pSegs.some(s => {
        const label = (s.behavior?.label || s.behavior?.id || '').toLowerCase()
        return label === 'implementing' || label === 'debugging'
      })
      if (!hadCode) consecutiveNoEffort++
      else break
    }

    // Query gap history up to this point
    const queryGapsMs = []
    for (let k = 1; k <= qIdx; k++) {
      queryGapsMs.push(Math.round(queryEvents[k].timestamp - queryEvents[k - 1].timestamp))
    }

    return {
      query_index: qIdx,
      query_text: queryContent,
      query_time: Math.round(queryRelTime),
      window_start: Math.round(windowStartRel),
      window_duration_ms: Math.round(windowDuration),
      behavior_sequence: behaviorSequence,
      terminal_runs_in_window: windowRunCount,
      errors_in_window: windowErrorCount,
      tests_passed_before_query: lastTest?.payload?.passed_count ?? 0,
      tests_total: lastTest?.payload?.total_tests ?? 0,

      // Context features
      query_number: qIdx + 1,
      queries_so_far: qIdx,
      time_since_session_start_ms: Math.round(queryTimestamp - sessionStart),
      time_since_last_query_ms: qIdx > 0 ? Math.round(queryTimestamp - queryEvents[qIdx - 1].timestamp) : null,
      previous_pattern: previousPattern,
      consecutive_no_effort: consecutiveNoEffort,
      query_gaps_ms: queryGapsMs,
    }
  })

  // ── 3. Session-Level Profile ──

  // Time per behavior
  const timePerBehavior = {}
  segments.forEach(s => {
    const label = (s.behavior?.label || s.behavior?.id || 'unknown').toLowerCase()
    timePerBehavior[label] = (timePerBehavior[label] || 0) + Math.round(s.endTime - s.startTime)
  })

  // Pct per behavior
  const totalSegTime = Object.values(timePerBehavior).reduce((a, b) => a + b, 0) || 1
  const pctPerBehavior = {}
  Object.entries(timePerBehavior).forEach(([k, v]) => {
    pctPerBehavior[k] = Math.round((v / totalSegTime) * 100) / 100
  })

  // Transition counts
  const transitionCounts = {}
  for (let i = 0; i < segments.length - 1; i++) {
    const from = (segments[i].behavior?.label || segments[i].behavior?.id || 'unknown').toLowerCase()
    const to = (segments[i + 1].behavior?.label || segments[i + 1].behavior?.id || 'unknown').toLowerCase()
    if (from !== to) {
      const key = `${from}->${to}`
      transitionCounts[key] = (transitionCounts[key] || 0) + 1
    }
  }

  const timeToFirstQuery = firstQueryMs != null ? Math.round(firstQueryMs) : null

  const sessionProfile = {
    user_id: userId,
    student_name: studentName,
    total_duration_ms: Math.round(replayDuration || 0),
    total_segments: segments.length,
    total_queries: queryEvents.length,
    total_terminal_runs: terminalRuns.length,
    total_terminal_errors: terminalErrors.length,
    time_to_first_query_ms: timeToFirstQuery,
    time_per_behavior: timePerBehavior,
    pct_per_behavior: pctPerBehavior,
    transition_counts: transitionCounts,
    outcome: {
      all_tests_passed: allPassed,
      final_passed: finalPassed,
      final_total: finalTotal,
      final_pass_rate: finalTotal > 0 ? Math.round((finalPassed / finalTotal) * 100) : 0,
      total_time_ms: Math.round(sessionEnd - sessionStart),
    },
  }

  return {
    session_profile: sessionProfile,
    labeled_segments: labeledSegments,
    query_windows: queryWindows,
  }
}


// ── Main export function ──────────────────────────────────────────────────────

export function buildPerStudentExport(analyticsById, students, sessionId) {
  const allStudentData = []

  // Build a name lookup from students array
  const nameMap = {}
  if (students) {
    students.forEach(s => {
      const name = s.display_name || s.username
        || `${s.first_name || ''} ${s.last_name || ''}`.trim()
        || 'Unknown'
      nameMap[s.id] = name
      nameMap[String(s.id)] = name
    })
  }

  Object.entries(analyticsById).forEach(([userId, entry]) => {
    const { analytics, rawEvents } = entry
    if (!analytics || !analytics.filteredEvents || analytics.filteredEvents.length === 0) return

    const studentName = nameMap[userId] || `Student ${userId}`

    const studentData = buildStudentExport(userId, studentName, analytics, rawEvents)
    if (studentData) {
      allStudentData.push(studentData)
    }
  })

  console.log(`Built per-student export: ${allStudentData.length} students`)
  return allStudentData
}


// ── Download as JSON ──────────────────────────────────────────────────────────

export function downloadJSON(data, filename = 'student_export.json') {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}